import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Upload,
    QrCode,
    History,
    Check,
    X,
    Trash2,
    RefreshCw,
    AlertCircle,
    Image as ImageIcon,
    Eye,
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { formatDate, formatRelativeTime } from '@/lib/utils';
import toast from 'react-hot-toast';
import api from '@/lib/api';

interface PaymentQR {
    _id: string;
    qrImageUrl: string;
    upiId: string;
    merchantName: string;
    isActive: boolean;
    createdBy?: {
        name: string;
        email: string;
    };
    createdAt: string;
    deactivatedAt?: string;
}

export default function QRManagement() {
    const [activeQR, setActiveQR] = useState<PaymentQR | null>(null);
    const [qrHistory, setQrHistory] = useState<PaymentQR[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);
    const [selectedQR, setSelectedQR] = useState<PaymentQR | null>(null);

    // Upload form state
    const [qrFile, setQrFile] = useState<File | null>(null);
    const [qrPreview, setQrPreview] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [activeRes, historyRes] = await Promise.all([
                api.get('/admin/payment/qr/active'),
                api.get('/admin/payment/qr/history'),
            ]);

            setActiveQR(activeRes.data.data);
            setQrHistory(historyRes.data.data.qrCodes || []);
        } catch (error) {
            console.error('Failed to fetch QR data:', error);
            toast.error('Failed to load QR data');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('File size must be less than 5MB');
                return;
            }
            setQrFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setQrPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUpload = async () => {
        if (!qrFile || !qrPreview) {
            toast.error('Please select a QR code image');
            return;
        }

        setUploading(true);
        try {
            await api.post('/admin/payment/qr', {
                imageBase64: qrPreview,
                merchantName: 'Gaming Platform',
            });

            toast.success('Payment QR uploaded successfully! UPI ID extracted.');
            setShowUploadDialog(false);
            setQrFile(null);
            setQrPreview('');
            fetchData();
        } catch (error: any) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Failed to upload QR');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedQR) return;

        try {
            await api.delete(`/admin/payment/qr/${selectedQR._id}`);
            toast.success('QR code deleted successfully');
            setShowDeleteDialog(false);
            setSelectedQR(null);
            fetchData();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete QR');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white">QR Code Management</h1>
                    <p className="text-gray-400 mt-1">
                        Manage payment QR codes for manual deposits
                    </p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={fetchData}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                    <button
                        onClick={() => setShowUploadDialog(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-500 transition-colors"
                    >
                        <Upload className="w-4 h-4" />
                        Upload New QR
                    </button>
                </div>
            </div>

            {/* Active QR Card */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-violet-900/50 to-purple-900/50 border border-violet-500/30 rounded-xl p-6"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                        <Check className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-white">Active Payment QR</h2>
                        <p className="text-sm text-gray-400">Currently being used for deposits</p>
                    </div>
                </div>

                {activeQR ? (
                    <div className="flex justify-center">
                        {/* QR Preview */}
                        <div className="flex flex-col items-center p-6 bg-white rounded-xl">
                            <img
                                src={activeQR.qrImageUrl}
                                alt="Payment QR Code"
                                className="w-64 h-64 object-contain"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200?text=QR+Image';
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center py-12 text-center">
                        <div className="p-4 bg-yellow-500/20 rounded-full mb-4">
                            <AlertCircle className="w-8 h-8 text-yellow-400" />
                        </div>
                        <h3 className="text-lg font-medium text-white">No Active QR Code</h3>
                        <p className="text-gray-400 mt-2 max-w-md">
                            There is no active payment QR code. Users will not be able to make deposits until you upload one.
                        </p>
                        <button
                            onClick={() => setShowUploadDialog(true)}
                            className="mt-4 flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-500 transition-colors"
                        >
                            <Upload className="w-4 h-4" />
                            Upload QR Code
                        </button>
                    </div>
                )}
            </motion.div>

            {/* QR History */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gray-800/50 border border-gray-700 rounded-xl p-6"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-gray-700 rounded-lg">
                        <History className="w-5 h-5 text-gray-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">QR Code History</h2>
                </div>

                {qrHistory.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-700">
                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Preview</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Status</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Created</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {qrHistory.map((qr) => (
                                    <tr key={qr._id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                                        <td className="py-3 px-4">
                                            <button
                                                onClick={() => {
                                                    setSelectedQR(qr);
                                                    setShowPreviewDialog(true);
                                                }}
                                                className="w-12 h-12 bg-white rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-violet-500 transition-all"
                                                title="Click to preview"
                                            >
                                                <img
                                                    src={qr.qrImageUrl}
                                                    alt="QR"
                                                    className="w-full h-full object-contain"
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48?text=QR';
                                                    }}
                                                />
                                            </button>
                                        </td>
                                        <td className="py-3 px-4">
                                            {qr.isActive ? (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                                                    <Check className="w-3 h-3" />
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-500/20 text-gray-400 text-xs font-medium rounded-full">
                                                    <X className="w-3 h-3" />
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="text-gray-300">{formatDate(qr.createdAt)}</span>
                                            <br />
                                            <span className="text-gray-500 text-xs">{formatRelativeTime(qr.createdAt)}</span>
                                        </td>
                                        <td className="py-3 px-4">
                                            {!qr.isActive && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedQR(qr);
                                                        setShowDeleteDialog(true);
                                                    }}
                                                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                                                    title="Delete QR"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-400">
                        <QrCode className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No QR codes have been uploaded yet</p>
                    </div>
                )}
            </motion.div>

            {/* Upload Dialog */}
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                <DialogContent className="bg-gray-800 border-gray-700 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Upload className="w-5 h-5" />
                            Upload New Payment QR
                        </DialogTitle>
                        <DialogDescription className="text-gray-400">
                            This will deactivate the current QR. The system will automatically detect the UPI ID from the QR code image.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                QR Code Image *
                            </label>

                            {!qrPreview ? (
                                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-600 border-dashed rounded-lg hover:border-violet-500 transition-colors cursor-pointer relative">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    />
                                    <div className="space-y-1 text-center pointer-events-none">
                                        <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                                        <div className="flex text-sm text-gray-300 justify-center">
                                            <span className="text-violet-400 hover:text-violet-300">Upload a file</span>
                                            <p className="pl-1">or drag and drop</p>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            PNG, JPG up to 5MB
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-4 p-4 bg-gray-700/50 rounded-lg relative group">
                                    <button
                                        onClick={() => {
                                            setQrFile(null);
                                            setQrPreview('');
                                        }}
                                        className="absolute top-2 right-2 p-1 bg-red-500/80 rounded-full hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                    <p className="text-sm text-gray-400 mb-2">Preview:</p>
                                    <div className="flex items-center gap-4">
                                        <div className="w-24 h-24 bg-white rounded-lg overflow-hidden flex-shrink-0">
                                            <img
                                                src={qrPreview}
                                                alt="QR Preview"
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">Gaming Platform</p>
                                            <p className="text-gray-400 text-sm italic">UPI ID will be extracted automatically</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>


                    </div>

                    <DialogFooter className="gap-3">
                        <button
                            onClick={() => setShowUploadDialog(false)}
                            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleUpload}
                            disabled={uploading || !qrFile}
                            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {uploading ? (
                                <>
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4" />
                                    Upload & Scan
                                </>
                            )}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="bg-gray-800 border-gray-700">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-red-400" />
                            Delete QR Code
                        </DialogTitle>
                        <DialogDescription className="text-gray-400">
                            Are you sure you want to delete this QR code? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>

                    {selectedQR && (
                        <div className="flex items-center gap-4 py-4">
                            <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0">
                                <img
                                    src={selectedQR.qrImageUrl}
                                    alt="QR"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <div>
                                <p className="text-white font-medium">{selectedQR.merchantName}</p>
                                <p className="text-gray-400 text-sm max-w-[200px] truncate">{selectedQR.upiId}</p>
                                <p className="text-gray-500 text-xs mt-1">
                                    Created: {formatDate(selectedQR.createdAt)}
                                </p>
                            </div>
                        </div>
                    )}

                    <DialogFooter className="gap-3">
                        <button
                            onClick={() => setShowDeleteDialog(false)}
                            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDelete}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
                <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Eye className="w-5 h-5" />
                            QR Code Preview
                        </DialogTitle>
                    </DialogHeader>

                    {selectedQR && (
                        <div className="flex flex-col items-center py-4">
                            <div className="p-4 bg-white rounded-xl">
                                <img
                                    src={selectedQR.qrImageUrl}
                                    alt="QR Preview"
                                    className="w-64 h-64 object-contain"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/256?text=QR+Image';
                                    }}
                                />
                            </div>
                            <p className="text-gray-400 text-sm mt-4">
                                Created: {formatDate(selectedQR.createdAt)}
                            </p>
                        </div>
                    )}

                    <DialogFooter>
                        <button
                            onClick={() => setShowPreviewDialog(false)}
                            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            Close
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
